import React from "react";

/**
 * LR Direct ATLAS card primitives.
 * Visual clone of the LaneRunner ATLAS Hub card shell — intentionally duplicated
 * (NOT imported from src/components/post-load/*) so LR Direct stays fully decoupled.
 */
export function LRDirectAtlasCard({
  children,
  className = "",
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`rounded-xl border border-border bg-card ${className}`}
      style={{ boxShadow: "var(--shadow-card)", ...style }}
    >
      {children}
    </div>
  );
}

export function LRDirectAtlasCardHeader({
  icon,
  title,
  children,
  bg = "#02ffd3",
  borderColor = "hsl(170 60% 45% / 0.3)",
  fg = "hsl(170 50% 15%)",
}: {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  bg?: string;
  borderColor?: string;
  fg?: string;
}) {
  return (
    <div
      className="flex items-center justify-between px-4 py-3 border-b transition-colors"
      style={{ backgroundColor: bg, borderColor }}
    >
      <div className="flex items-center gap-2">
        {icon}
        <h4
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ color: fg }}
        >
          {title}
        </h4>
      </div>
      {children}
    </div>
  );
}

export interface ToolStat {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface ToolRow {
  label: string;
  value: string;
}

/**
 * Visual-only tool card matching the LaneRunner ATLAS tool-card design:
 * atlas header → status banner → description → 3-up stat grid → list → CTA.
 * Not wired to any data source. Placeholder until LR Direct logic is built.
 */
export function LRDirectComingSoonToolCard({
  icon: Icon,
  title,
  bannerIcon: BannerIcon,
  bannerText,
  bannerTone = "teal",
  description,
  stats,
  listTitle,
  rows,
  ctaLabel,
  children,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string;
  bannerIcon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  bannerText: string;
  bannerTone?: "teal" | "amber" | "blue";
  description: string;
  stats: ToolStat[];
  listTitle: string;
  rows: ToolRow[];
  ctaLabel: string;
  children?: React.ReactNode;
}) {
  const tones = {
    teal: { bg: "hsl(var(--brand-teal-deep) / 0.10)", fg: "hsl(var(--brand-teal-deep))" },
    amber: { bg: "hsl(35 95% 50% / 0.10)", fg: "hsl(35 80% 42%)" },
    blue: { bg: "hsl(210 90% 50% / 0.08)", fg: "hsl(210 70% 42%)" },
  }[bannerTone];

  return (
    <LRDirectAtlasCard className="flex flex-col">
      <LRDirectAtlasCardHeader
        icon={<Icon className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title={title}
      >
        <span
          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ backgroundColor: "hsl(170 50% 15% / 0.12)", color: "hsl(170 50% 15%)" }}
        >
          Soon
        </span>
      </LRDirectAtlasCardHeader>

      <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">
        <div
          className="rounded-md px-3 py-2 flex items-center gap-2"
          style={{ backgroundColor: tones.bg }}
        >
          <BannerIcon className="h-3 w-3 shrink-0" style={{ color: tones.fg }} />
          <span className="text-[11px] font-semibold" style={{ color: tones.fg }}>
            {bannerText}
          </span>
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">{description}</p>

        {children}

        <div className="grid grid-cols-3 gap-1.5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-md px-2 py-1.5 bg-muted/50">
              <div className="flex items-center gap-1 mb-0.5">
                <s.icon className="h-2.5 w-2.5 text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground uppercase tracking-wide truncate">
                  {s.label}
                </span>
              </div>
              <span className="text-xs font-semibold text-muted-foreground/70">{s.value}</span>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">
            {listTitle}
          </span>
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: "hsl(var(--muted-foreground) / 0.4)" }}
                />
                <span className="text-muted-foreground font-medium truncate">{r.label}</span>
              </div>
              <span className="text-[10px] text-muted-foreground/70 shrink-0">{r.value}</span>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-1">
          <button
            disabled
            className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 cursor-not-allowed opacity-70"
            style={{
              backgroundColor: "hsl(170 85% 32% / 0.08)",
              color: "hsl(var(--brand-teal-deep))",
            }}
          >
            {ctaLabel}
            <span className="ml-auto text-[9px] uppercase tracking-wider text-muted-foreground">
              Coming soon
            </span>
          </button>
        </div>
      </div>
    </LRDirectAtlasCard>
  );
}
